<template>
  <section>
    <h1>Testimonials</h1>
    <v-layout row>
      <v-flex xs12>
        <v-dialog persistent class="my-3" v-model="modalWindowVisible" max-width="500px">
            <v-btn color="primary" dark slot="activator" class="mb-2" v-if="isWriteAvailable">Add Item</v-btn>
            <v-card>
              <v-card-title>
                <span class="headline">{{ formTitle }} </span>
              </v-card-title>
              <v-card-media
                v-if="formData.image.url"
                :src="formData.image.url"
                height="200px"
              />
              <v-card-text>
                <v-container grid-list-md>
                  <v-layout wrap>
                    <v-flex xs12>
                      <content-field
                        label="Name"
                        v-model="formData.name"
                        :error="$v.formData.name"
                      ></content-field>
                    </v-flex>
                    <v-flex xs12>
                      <content-field
                        label="Position"
                        v-model="formData.jobTitle"
                        :error="$v.formData.jobTitle"
                      ></content-field>
                    </v-flex>
                    <v-flex xs12>
                      <v-card>
                        <content-editor
                          placeholder="Testimonial"
                          v-model="formData.testimonialBody"
                          :error="$v.formData.testimonialBody"
                        />
                      </v-card>
                    </v-flex>
                    <v-flex xs12>
                      <v-btn
                        color="success"
                        class="ml-0"
                        @click="toggleGalleryModal"
                      >
                        Choose Image
                      </v-btn>
                      <v-btn
                        :disabled="!formData.image.url"
                        color="success"
                        @click="removeImage"
                      >
                        Remove Image
                      </v-btn>
                      <div class="red--text" v-if="errorImageCheck">Please select image</div>
                    </v-flex>
                  </v-layout>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer/>
                <v-btn color="blue darken-1" flat @click.stop="closeModal">Cancel</v-btn>
                <v-btn color="blue darken-1" flat @click.stop="save">Save</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <v-dialog v-model="modalGalleryVisible">
            <galleries-list modalView="true" @pickedImage="setAvatar"/>
          </v-dialog>

        <v-card>
          <v-list>
            <template v-for="item in testimonials">
              <v-list-tile :key="item.id" class="testimonial my-3">
                <v-list-tile-avatar>
                  <img :src="item.galleryItem.imagesUrls.originalImageUrl">
                </v-list-tile-avatar>
                <v-list-tile-content>
                  <v-list-tile-title v-html="item.name"></v-list-tile-title>
                  <v-list-tile-sub-title v-html="item.jobTitle"></v-list-tile-sub-title>
                  <v-list-tile-sub-title v-html="item.testimonialBody"></v-list-tile-sub-title>
                </v-list-tile-content>
                <v-tooltip bottom>
                  <v-btn
                    color="lime"
                    fab flat
                    slot="activator"
                    @click="edit(item)"
                    :disabled="!isWriteAvailable"
                  >
                    <v-icon>edit</v-icon>
                  </v-btn>
                  <span>Edit</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <v-btn
                    color="error"
                    fab flat
                    slot="activator"
                    @click="remove(item.id)"
                    :disabled="!isWriteAvailable"
                  >
                    <v-icon>delete</v-icon>
                  </v-btn>
                  <span>Delete</span>
                </v-tooltip>
              </v-list-tile>
            </template>
          </v-list>
        </v-card>
      </v-flex>
    </v-layout>
  </section>
</template>

<script>
import ContentField from '@/components/shared/ContentField'
import ContentEditor from '@/components/shared/ContentEditor/index.vue'
import GalleriesList from '@/components/pages/Gallery/GalleriesList'
import { mapGetters, mapActions } from 'vuex'
import { required, maxLength } from 'vuelidate/lib/validators'
import { clone, omit } from 'ramda'
import mixins from '@/utils/mixins'

const defaultFormData = {
  id: '',
  name: '',
  jobTitle: '',
  testimonialBody: '',
  image: {}
}

export default {
  name: 'Testimonials',
  mixins: [mixins.isWriteAvailable],
  components: {
    ContentField,
    ContentEditor,
    GalleriesList
  },
  data () {
    return {
      formData: clone(defaultFormData),
      isItemEditing: false,
      modalWindowVisible: false,
      modalGalleryVisible: false,
      // DON'T DELETE! need for isWriteAvailable mixin
      scopeForWrite: 'general_settings:write'
    }
  },
  computed: {
    ...mapGetters({
      testimonials: 'getTestimonials'
    }),
    formTitle () {
      return this.isItemEditing ? 'Edit testimonial': 'Add testimonial'
    },
    errorImageCheck () {
      return this.$v.formData.image.hasOwnProperty('url') && this.$v.formData.image.url.$invalid && this.$v.$dirty
    },
  },
  watch: {
    modalWindowVisible (val) {
      if(!val) this.closeModal()
    }
  },
  methods: {
    ...mapActions(['fetchTestimonials', 'deleteTestimonial', 'updateTestimonial', 'createTestimonial']),
    setAvatar (item) {
      this.formData.image = {
        id: item.id,
        url: item.imagesUrls.originalImageUrl
      }
      this.modalGalleryVisible = false
    },
    removeImage () {
      this.formData.image = {}
    },
    toggleGalleryModal () {
      this.modalGalleryVisible = !this.modalGalleryVisible
    },
    closeModal () {
      this.modalWindowVisible = false
      this.$v.$reset()
      this.formData = clone(defaultFormData)
      this.isItemEditing = false
    },
    edit (testimonial) {
      this.formData = {
        id: testimonial.id,
        name: testimonial.name,
        jobTitle: testimonial.jobTitle,
        testimonialBody: testimonial.testimonialBody,
        image: {
          url: testimonial.galleryItem.imagesUrls.originalImageUrl
        }
      }

      this.isItemEditing = true
      this.modalWindowVisible = true
    },
    async save () {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      const payload = omit(['image'], clone(this.formData))

      if (this.formData.image.id) {
        payload.galleryItemId = this.formData.image.id
      }
      if (this.isItemEditing) {
        try {
          await this.updateTestimonial(payload)
          this.closeModal()
          this.fetchTestimonials()
        } catch (err) {
          throw err
        }
      } else {
        try {
          await this.createTestimonial(payload)
          this.closeModal()
          this.fetchTestimonials()
        } catch (err) {
          throw err
        }
      }
    },
    async remove (id) {
      await this.deleteTestimonial(id)
      this.fetchTestimonials()
    }
  },
  async mounted () {
    await this.fetchTestimonials()
  },
  validations: {
    formData: {
      name: { required },
      jobTitle: { required },
      testimonialBody: { required, maxLength: maxLength(450) },
      image: { url: { required } }
    }
  }
}
</script>

<style lang="scss" scoped>
.testimonial {
  /deep/ .list__tile {
    height: auto;

    &__sub-title {
      white-space: normal;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/pages/StaticPages/Testimonials.vue